// Phase 20 · Community V3 · Live chat panel (polling 30s + Visibility API pause)
import React, { useState, useRef, useEffect } from "react";
import { Send, Flag, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import useEventPolling from "../../hooks/useEventPolling";
import MarketplaceEmbedCard from "../community/MarketplaceEmbedCard";
import MiniLandingEmbedCard from "../community/MiniLandingEmbedCard";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
    try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
}

function ChatBubble({ m, isSelf, canModerate, onModerate }) {
    return (
        <div className={`group flex ${isSelf ? "justify-end" : "justify-start"} px-3 py-1`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${isSelf ? "bg-white text-neutral-900" : "bg-[#1A1A1A] text-neutral-100"}`} data-testid={`ch-msg-${m.id}`}>
                <div className="flex items-center gap-1 mb-1">
                    <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                        @{m.sender_username}
                    </p>
                    <span className="text-[9px] opacity-60 ml-1">{fmtTime(m.sent_at)}</span>
                    {canModerate && !isSelf && (
                        <button
                            type="button"
                            onClick={() => onModerate?.(m)}
                            className="ml-auto opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-[#FAFAFA]"
                            data-testid={`chat-mod-${m.id}`}
                        >
                            <MoreVertical size={12} />
                        </button>
                    )}
                </div>
                {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                {m.message_type === "asset_marketplace" && m.marketplace_preview && (
                    <div className="mt-1"><MarketplaceEmbedCard preview={m.marketplace_preview} /></div>
                )}
                {m.message_type === "mini_landing" && m.mini_landing_preview && (
                    <div className="mt-1"><MiniLandingEmbedCard preview={m.mini_landing_preview} /></div>
                )}
            </div>
        </div>
    );
}

export default function EventLiveChat({ event, viewer, canModerate = false }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const endRef = useRef(null);

    const canSend = event.status === "live" || event.status === "scheduled";
    const isRegistered = event.viewer_rsvp_status === "registered" || event.is_host;

    const { data, refetch, error } = useEventPolling(
        canSend ? `${API}/api/community/events/${event.id}/chat?limit=100` : null,
        { enabled: canSend }
    );
    const messages = ((data?.items || []).slice().reverse());

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    const send = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/events/${event.id}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ message_type: "text", body: text.trim() }),
            });
            if (!r.ok) {
                if (r.status === 403) toast.error("RSVP required to chat");
                else if (r.status === 429) toast.error("Slow down");
                else toast.error("Send failed");
                return;
            }
            setText("");
            refetch();
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    const moderate = async (m) => {
        if (!canModerate) return;
        try {
            const r = await fetch(`${API}/api/community/chat/${m.id}/moderate?action=hide&reason=host_moderation`, {
                method: "POST",
                credentials: "include",
            });
            if (r.ok) { toast.success("Message hidden"); refetch(); }
            else toast.error("Moderate failed");
        } catch {}
    };

    if (error?.disabled) {
        return <p className="text-sm text-neutral-500 italic text-center py-4" data-testid="chat-disabled">Chat disabled</p>;
    }

    return (
        <div className="flex flex-col h-full bg-[#111111] border border-[#1A1A1A] rounded-2xl overflow-hidden" data-testid="event-live-chat">
            <div className="px-3 py-2 border-b border-[#1A1A1A]">
                <p className="text-xs uppercase tracking-widest text-neutral-400 font-semibold">
                    Live chat · {messages.length}
                </p>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
                {!messages.length && (
                    <p className="text-xs text-neutral-500 italic text-center py-6">
                        No messages yet — be the first!
                    </p>
                )}
                {messages.map((m) => (
                    <ChatBubble
                        key={m.id}
                        m={m}
                        isSelf={m.sender_id === viewer?.id}
                        canModerate={canModerate}
                        onModerate={moderate}
                    />
                ))}
                <div ref={endRef} />
            </div>
            {canSend && (
                <div className="p-2 border-t border-[#1A1A1A]">
                    {!isRegistered && !event.is_host && (
                        <p className="text-[11px] text-neutral-500 italic mb-1 px-1">
                            RSVP to join the chat.
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                            placeholder={isRegistered || event.is_host ? "Say something..." : "RSVP required"}
                            disabled={!isRegistered && !event.is_host}
                            maxLength={1000}
                            className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] disabled:opacity-50"
                            data-testid="event-chat-input"
                        />
                        <button
                            type="button"
                            onClick={send}
                            disabled={!text.trim() || busy || (!isRegistered && !event.is_host)}
                            className="px-3 py-2 bg-white text-neutral-900 font-semibold rounded-lg disabled:opacity-40"
                            data-testid="event-chat-send"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
